import Table from '@amzn/meridian/table/table';
import TableCell from '@amzn/meridian/table/table-cell';
import TableRow from '@amzn/meridian/table/table-row';
import React, { useContext, useState, useEffect } from 'react';
import { StateContext } from '../../GlobalStateStore';
import { getRequesterRole } from './helper';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import Pagination from '@amzn/meridian/pagination/pagination';
import DataState from '../../Types/DataState';
import Column from '@amzn/meridian/column/column';
import { StatsCoachingNote } from '../../Types';
import Link from '@amzn/meridian/link';
import { urls } from '../../Util';
import {
  CoachingsTableSortColumn,
  errorMessageBox,
  tableLoader,
} from './types';
import Tag from '@amzn/meridian/tag/tag';
import {
  coachingsTableCustomSort,
  getCoachingNoteStatus,
  getUserFromIdWithPrefix,
} from './OmCoachingHelper';
import Row from '@amzn/meridian/row/row';
import { SortDirection } from '../../Types/SortDirection';

interface CoachingsTableProps {
  coachings: StatsCoachingNote[];
  statsState: string;
}

const CoachingsTable: React.FC<CoachingsTableProps> = ({
  coachings,
  statsState,
}): React.ReactElement => {
  const intl = useIntl();
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, isOm, requesterUserID, requesterUsername } =
    inspireOptions.data;
  const requestorRole = getRequesterRole(isOm, isTm);

  const [currentPage, setCurrentPage] = useState(1);
  const COACHINGS_PER_PAGE = 15;
  const numberOfPages = Math.ceil(coachings.length / COACHINGS_PER_PAGE);
  const firstVisibleIndex = (currentPage - 1) * COACHINGS_PER_PAGE;
  const lastVisibleIndex = firstVisibleIndex + COACHINGS_PER_PAGE;
  const [sortColumn, setSortColumn] = useState(CoachingsTableSortColumn.DATE);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.DESCENDING
  );
  const onSort = ({ sortColumn, sortDirection }) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  };

  coachings.sort((a, b) => {
    return coachingsTableCustomSort(a, b, sortColumn, sortDirection);
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [coachings]);

  return (
    <Column width='100%'>
      <Row minHeight={'710px'} alignmentVertical='top'>
        <Table
          headerRows={1}
          showDividers
          showStripes
          spacing='small'
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
        >
          <TableRow>
            <TableCell width='28%'>
              <FormattedMessage
                id='cs_perfportal_coaching_page_coachings_table_header_coaching_id'
                defaultMessage='Coaching ID'
              />
            </TableCell>
            <TableCell
              sortColumn={CoachingsTableSortColumn.ASSOCIATE_NAME}
              width='22%'
            >
              <FormattedMessage
                id='cs_perfportal_coaching_page_coachings_table_header_associate'
                defaultMessage='Associate'
              />
            </TableCell>
            <TableCell sortColumn={CoachingsTableSortColumn.DATE} width='28%'>
              <FormattedMessage
                id='cs_perfportal_coaching_page_coachings_table_header_date'
                defaultMessage='Date'
              />
            </TableCell>
            <TableCell
              sortColumn={CoachingsTableSortColumn.COACHING_STATUS}
              width='22%'
            >
              <FormattedMessage
                id='cs_perfportal_coaching_page_coachings_table_header_status'
                defaultMessage='Status'
              />
            </TableCell>
          </TableRow>
          {statsState === DataState.LOADED &&
            coachings
              .slice(firstVisibleIndex, lastVisibleIndex)
              .map(coaching => (
                <TableRow>
                  <TableCell>
                    <Link
                      type='secondary'
                      href={`${urls.coachingNotes}/${getUserFromIdWithPrefix(
                        coaching.recipient.userID
                      )}?coachingID=${coaching.coachingId}`}
                      data-csa-c-type='link'
                      data-csa-c-slot-id='om-coaching-page-view-coaching-slot'
                      data-csa-c-content-id={`om-coaching-page-view-coaching-link-for-${coaching.coachingId}`}
                      data-csa-c-noteid={coaching.coachingId}
                      data-csa-c-tag1='OM Coaching'
                      data-csa-c-profile-id={requesterUserID}
                      data-csa-c-username={requesterUsername}
                      data-csa-c-requester-role={requestorRole}
                    >
                      {coaching.coachingId}
                    </Link>
                  </TableCell>
                  <TableCell>{coaching.recipient.userName}</TableCell>
                  <TableCell>
                    <FormattedDate
                      value={new Date(coaching.deliveredAt)}
                      dateStyle='long'
                      timeZone='UTC'
                    />
                  </TableCell>
                  <TableCell>
                    <Tag type={`${getCoachingNoteStatus(coaching).tagType}`}>
                      {intl.formatMessage({
                        id: `${getCoachingNoteStatus(coaching).id}`,
                        defaultMessage: `${
                          getCoachingNoteStatus(coaching).tagText
                        }`,
                      })}
                    </Tag>
                  </TableCell>
                </TableRow>
              ))}
        </Table>
      </Row>
      {statsState === DataState.LOADING && tableLoader}
      {statsState === DataState.ERROR && errorMessageBox}
      <Row alignmentVertical='bottom' alignmentHorizontal='center'>
        <Pagination
          showSkipArrows
          numberOfPages={numberOfPages}
          onChange={setCurrentPage}
          currentPage={currentPage}
        />
      </Row>
      {coachings.length === 0 && statsState === DataState.LOADED ? (
        <Row alignmentHorizontal='center'>
          <FormattedMessage
            id='cs_perfportal_coaching_page_no_coachings'
            defaultMessage='No coachings found for the selected month!'
          />
        </Row>
      ) : null}
    </Column>
  );
};

export default CoachingsTable;
